import { Link } from 'react-router-dom';
import { Plus, FileText, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { useInsurancePolicies } from '@/hooks/use-comply';
import { useOrgToday } from '@/hooks/use-settings';
import { formatMoney } from '@/lib/currency';
import { formatDateOnly } from '@/lib/date-utils';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import type { InsurancePolicy } from '@/lib/types';

function ExpiryBadge({ policy }: { policy: InsurancePolicy }) {
  const orgToday = useOrgToday();
  const daysUntilExpiry = differenceInCalendarDays(
    parseISO(policy.expiry_date),
    parseISO(orgToday)
  );

  if (daysUntilExpiry < 0) {
    return <Badge className="bg-red-100 text-red-800">Expired</Badge>;
  }
  if (daysUntilExpiry <= 60) {
    return (
      <Badge className="bg-yellow-100 text-yellow-800">
        Expiring in {daysUntilExpiry}d
      </Badge>
    );
  }
  return <Badge className="bg-green-100 text-green-800">Valid</Badge>;
}

function RopeAccessBadge({ covers }: { covers: boolean }) {
  return covers ? (
    <Badge className="bg-blue-100 text-blue-800">Rope Access Covered</Badge>
  ) : (
    <Badge variant="outline" className="text-gray-600">
      No Rope Access
    </Badge>
  );
}

export function InsurancePage() {
  const orgToday = useOrgToday();
  const { data: policies, isLoading } = useInsurancePolicies();

  const expiredCount =
    policies?.filter((p) => p.expiry_date < orgToday).length ?? 0;
  const expiringCount =
    policies?.filter((p) => {
      const days = differenceInCalendarDays(
        parseISO(p.expiry_date),
        parseISO(orgToday)
      );
      return days >= 0 && days <= 60;
    }).length ?? 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Insurance Register
          </h1>
          <p className="text-gray-600 mt-1">
            QP-300 Clause 2.5 — Liability and insurance policy tracking
          </p>
        </div>
        <Button asChild>
          <Link to="/compliance/insurance/new">
            <Plus className="h-4 w-4 mr-2" />
            Add Policy
          </Link>
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{policies?.length ?? '-'}</p>
                <p className="text-sm text-gray-600">Total Policies</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={expiringCount > 0 ? 'border-yellow-200 bg-yellow-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold">{expiringCount}</p>
                <p className="text-sm text-gray-600">Expiring Soon</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={expiredCount > 0 ? 'border-red-200 bg-red-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-8 w-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold">{expiredCount}</p>
                <p className="text-sm text-gray-600">Expired</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : !policies || policies.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-600 font-medium">No insurance policies yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Add your first policy to begin Clause 2.5 compliance tracking
            </p>
            <Button asChild className="mt-4">
              <Link to="/compliance/insurance/new">
                <Plus className="h-4 w-4 mr-2" />
                Add Policy
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Policy</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Insurer</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead className="hidden sm:table-cell">Rope Access</TableHead>
                <TableHead className="hidden md:table-cell">Coverage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {policies.map((policy) => (
                <TableRow key={policy.id} className="hover:bg-gray-50">
                  <TableCell>
                    <span className="font-medium text-gray-900">
                      {policy.policy_name}
                    </span>
                    <p className="text-xs text-gray-500">{policy.policy_number}</p>
                  </TableCell>
                  <TableCell className="text-sm">
                    {policy.policy_type_display}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {policy.insurer}
                  </TableCell>
                  <TableCell>
                    <ExpiryBadge policy={policy} />
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formatDateOnly(policy.expiry_date)}
                    </p>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <RopeAccessBadge covers={policy.covers_rope_access} />
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-gray-600">
                    {policy.coverage_amount
                      ? formatMoney(policy.coverage_amount, policy.currency)
                      : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
